"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useLocalizedRouter } from "@/lib/localized-router";
import Link from "@/lib/localized-link";
import { useLocale, useT } from "@/lib/locale-context";
import HomeBlogSection, { type HomepageBlogPost } from "@/components/home/HomeBlogSection";
import { formatPublishedAt, categoryLabel } from "@/lib/blog-format";
import { useAuth } from "@/lib/auth-context";
import { fetchFeaturedListing, fetchAPI, magicSearch } from "@/lib/api";
import { shared } from "@/lib/shared-get";
import { isEverythingQuery, plainParams } from "@/lib/magic-plain";
import { trackEvent } from "@/lib/tracking";
import SharedHeader from "@/components/SharedHeader";
import PWAHomeHelper from "@/components/PWAHomeHelper";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import TrialOfferCard from "@/components/TrialOfferCard";
import PWAInstallPromo from "@/components/PWAInstallPromo";
import RecentlyAnalyzedCarousel from "@/components/RecentlyAnalyzedCarousel";
import HomeHeroSplit from "@/components/HomeHeroSplit";
import HomeCoreFlowsRow from "@/components/HomeCoreFlowsRow";
// The signed-in hub (widgets, drag-and-drop layout, its own hero map) is
// a separate chunk: anonymous visitors never render it, and as a static
// import it put ~55 KB gzipped (dnd-kit, the hub widgets) on every
// visitor's home page load. With the session cookie the server renders
// it (its frame) during the auth check, so the HTML preloads its chunks.
const SignedInHome = dynamic(() => import("@/components/home/SignedInHome"), {
  loading: () => <div className="mx-auto h-64 max-w-6xl px-4 pt-8 sm:px-6" />,
});
import VisitorForYou from "@/components/home/VisitorForYou";
import { getCachedIntent, primeHub, usePersonal, type Intent } from "@/lib/use-personal";
import { ChevronRight } from "lucide-react";
import { evaluationHref, looksLikeLink } from "@/lib/listing-url";

/** Prefill the box with a featured listing link (first-time visitors
 *  and accounts without an analysis, owner 2026-10-04). Off since
 *  2026-10-18: a stranger's URL had to be cleared before pasting, the
 *  placeholder is the example now (UI critique). The switch stays. */
const DEMO_PREFILL = false;
const SEEN_KEY = "menzil_home_seen";
function firstVisit(): boolean {
  try {
    if (localStorage.getItem(SEEN_KEY)) return false;
    localStorage.setItem(SEEN_KEY, "1");
    return true;
  } catch { return true; }
}

export type { HomepageBlogPost };

/**
 * Wraps any block of CSS-keyframe-animated content and toggles
 * `data-paused="true"` whenever the wrapper scrolls out of view. The
 * global rule in globals.css ([data-paused="true"] *) cascades the
 * pause down to every nested keyframe, killing off-screen animation
 * cost without unmounting + remounting the SVGs.
 *
 * Containment + layer strategy:
 *   - `contain: content` (= layout + paint + style) bounds the SVG's
 *     invalidation to this wrapper. Paint-only was not enough -
 *     during window resize, the SVG rasterization forced layout on
 *     the surrounding card, which cascaded to the grid. With layout
 *     containment each SVG is a sealed island.
 *   - `content-visibility: auto` + `contain-intrinsic-size` let the
 *     browser skip rendering the SVG entirely while it's offscreen,
 *     which is the heaviest single cost during a mid-page resize.
 *   - `transform: translateZ(0)` forces a compositor layer. Without
 *     it, every resize frame re-rasterized the SVG contents at the
 *     new CSS size (the viewBox stays constant but the rendered
 *     bitmap doesn't). With the layer, resize = GPU composite of the
 *     cached bitmap — the browser only re-rasterizes at a new scale
 *     once the drag settles, not on every intermediate frame. This
 *     is the single biggest fix for "lag when I drag-resize the
 *     window" on the homepage.
 */
/**
 * Returns `true` when the viewport matches `(max-width: 639.98px)` -
 * i.e. the Tailwind pre-`sm` breakpoint. SSR starts at `false` so the
 * initial HTML matches the desktop / crawler case; the effect flips
 * the value on mount. Used for content that has to change between
 * mobile and desktop (e.g. the hero placeholder, where the full
 * "Sale or rental listing URL…" gets truncated on narrow screens).
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 639.98px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return isMobile;
}

/**
 * Scroll-reveal wrapper for the audience zigzag: slides the block in
 * from its diagonal side (with a fade) the first time it enters the
 * viewport. IntersectionObserver disconnects after the first hit so
 * scrolling back up doesn't replay the animation. Reduced-motion
 * users get the content without the slide.
 */
function Reveal({
  children,
  from,
  className,
}: {
  children: React.ReactNode;
  from: "left" | "right";
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className ?? ""} transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:translate-x-0 motion-reduce:opacity-100 ${
        seen
          ? "opacity-100 translate-x-0"
          : `opacity-0 ${from === "right" ? "translate-x-10" : "-translate-x-10"}`
      }`}
    >
      {children}
    </div>
  );
}

function useResizePauseAnimations() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let timeoutId: number | undefined;
    const root = document.documentElement;
    // Stamp synchronously on the very first event of a stream so the
    // browser repaints the next frame already in the paused state. The
    // earlier rAF-defer always painted one full frame with active
    // animations before the pause kicked in, which was visible on the
    // first jolt of a drag-resize. Setting a single dataset property
    // is microsecond-cheap; no need to throttle.
    const onResize = () => {
      if (root.dataset.resizing !== "true") root.dataset.resizing = "true";
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        delete root.dataset.resizing;
      }, 160);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      if (timeoutId) window.clearTimeout(timeoutId);
      delete root.dataset.resizing;
    };
  }, []);
}

interface HomeClientProps {
  /** Server-rendered: what the product does, the market line, the short FAQ. */
  factsBlock?: React.ReactNode;
  /** Server-side hint that a session cookie exists: skips the optimistic
   *  prefill skeleton on the first paint (veterans never see it flash). */
  signedInHint?: boolean;
  /** Latest blog posts injected by the server-side page wrapper.
   *  Pre-fetched at build time from content/blog/. Empty array when
   *  no posts exist. */
  blogPosts?: HomepageBlogPost[];
}

/** Module-level memo of the signed-in prefill gate ("has this user
 *  ever run an analysis?"). Without it every SPA navigation home
 *  re-armed the eager preview skeleton, round-tripped /auth/onboarding
 *  and re-prefilled - a visible flash under the hero input each visit.
 *  null = not yet known this session. */
let _prefillGateMemo: boolean | null = null;

/** The demo URL the prefill last wrote. Seeding the input from it on
 *  remount lets the preview card paint in the first frame (its body is
 *  in the listing-preview cache) instead of skeleton-flashing through
 *  the effect + fetch round on every return to the homepage. */
let _prefillUrlMemo: string | null = null;

export default function HomeClient({ blogPosts = [], signedInHint = false, factsBlock }: HomeClientProps) {
  const t = useT();
  const { locale } = useLocale();
  const router = useLocalizedRouter();
  const { user, loading: authLoading } = useAuth();
  // With the session cookie the hub's first screen is read in one request
  // that starts now, beside /auth/me (the cookie is all the API needs),
  // not after it (lib/hub-boot). Asked during the hydration render, so it
  // goes out before the effects that start /auth/me; started once.
  if (signedInHint && authLoading && typeof window !== "undefined") primeHub(null);
  // What the visitor is here for (stated here or inferred from what they
  // browse, lib/use-personal): the hero subtitle, the "for you" band and
  // the tools order follow it. The last known intent is read from the
  // browser first so a returning visitor's page adapts without waiting.
  const { data: personal } = usePersonal();
  const [cachedIntent, setCachedIntent] = useState<Intent | null>(null);
  useEffect(() => { setCachedIntent(getCachedIntent()); }, []);
  const visitorIntent = personal ? personal.intent.effective : cachedIntent;
  const [heroUrl, setHeroUrl] = useState(() => _prefillUrlMemo ?? "");
  // Tracks the URL we wrote via the /featured-listing prefill so
  // the submit handler can tell "user clicked Analyze on the demo
  // we suggested" from "user typed / pasted their own URL". The
  // value is cleared on the first keystroke (see the input's
  // onChange below) so an edited-but-still-equal string still
  // counts as "typed".
  const [demoUrl, setDemoUrl] = useState<string | null>(() => _prefillUrlMemo);
  const isMobile = useIsMobile();
  useResizePauseAnimations();

  // Pre-fill the hero input with a featured listing URL for
  // anonymous first-time visitors. Reduces "I have nothing to
  // paste" friction. Tracks a ``prefillPending`` flag so the
  // HeroSplit's UrlPreviewCard slot can render an eager skeleton
  // from the first paint, preventing layout shift when the URL
  // lands and the preview card animates in.
  //
  // Initial value: ``true`` so the skeleton is in the DOM on the
  // very first render (SSR + client hydration), BEFORE the auth
  // context has resolved and BEFORE the fetch fires. The effect
  // below flips it to false the moment we know we don't need it
  // (signed-in user, or input already has a value). Without this
  // default the slot starts empty for one paint and the input
  // visibly jumps down when the prefill resolves.
  // Skip the eager skeleton entirely when this session has already
  // established that the signed-in user doesn't get the prefill, or
  // when the seeded demo URL already fills the slot.
  const [prefillPending, setPrefillPending] = useState(
    DEMO_PREFILL && !signedInHint && _prefillGateMemo !== false && _prefillUrlMemo === null,
  );
  // The featured-listing prefill: anonymous first-time visitors and
  // signed-in accounts without an analysis get the demo link; everyone
  // else gets the empty search box (owner, 2026-10-04).
  useEffect(() => {
    if (!DEMO_PREFILL) { setPrefillPending(false); return; }
    if (!authLoading && !user && !firstVisit()) { setPrefillPending(false); return; }
    if (authLoading) return; // wait until we know who the user is
    if (heroUrl) {
      setPrefillPending(false);
      return;
    }
    let cancelled = false;
    const gate = user
      ? _prefillGateMemo !== null
        ? Promise.resolve(_prefillGateMemo)
        // Shared with the hub roadmap's read of the same status.
        : shared("/auth/onboarding", () => fetchAPI<{ has_analysis: boolean }>("/auth/onboarding"), { reuseMs: 30_000 })
            .then((s) => {
              _prefillGateMemo = !s.has_analysis;
              return _prefillGateMemo;
            })
            .catch(() => false)
      : Promise.resolve(true);
    if (!user && _prefillGateMemo !== false) setPrefillPending(true);
    gate.then((should: boolean) => {
      if (cancelled || !should) {
        if (!cancelled) setPrefillPending(false);
        return;
      }
      setPrefillPending(true);
      fetchFeaturedListing()
      .then((data) => {
        if (cancelled) return;
        if (data.url && !heroUrl) {
          _prefillUrlMemo = data.url;
          setHeroUrl(data.url);
          setDemoUrl(data.url);
        }
      })
      .catch(() => {
        /* silent - empty input is the safe fallback */
      })
      .finally(() => {
        if (!cancelled) setPrefillPending(false);
      });
    });
    return () => {
      cancelled = true;
    };
    // ``heroUrl`` is intentionally omitted so a user who clears the
    // input doesn't trigger a re-fetch + re-prefill loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const [heroBusy, setHeroBusy] = useState(false);
  const submitHero = useCallback(() => {
    const value = heroUrl.trim();
    if (!value) return;
    // Words search the listings; a link is analysed (owner, 2026-10-04:
    // the homepage is the marketplace with the AI valuation, search
    // first). ``via`` distinguishes the origins:
    //   demo    - the prefilled featured URL (off since 2026-10-04)
    //   typed   - typed or edited
    //   search  - words, not a link
    //   paste   - PasteFromClipboard helper fired
    if (!looksLikeLink(value)) {
      trackEvent("cta.hero.submit", { via: "search" });
      // The listings page's own sentence parser ("Yasamal 2 otaq 300
      // minə qədər" -> place, rooms, price); the words themselves are
      // the fallback when it cannot be reached or finds no filter.
      setHeroBusy(true);
      // "Bakı", "evlər": everything we list, never a search of the
      // descriptions for the word.
      const fallback = isEverythingQuery(value) ? "/listings" : `/listings?q=${encodeURIComponent(value)}`;
      magicSearch(value)
        .then((res) => {
          const params = { ...(res.params ?? {}) } as Record<string, string | string[]>;
          if (Object.keys(params).length === 0) {
            const plain = plainParams(value);
            if (Object.keys(plain).length) { router.push(`/listings?${new URLSearchParams(plain).toString()}`); return; }
            router.push(fallback); return;
          }
          const rest = (res.unparsed || "").trim();
          if (rest.length >= 2 && rest.length <= 60) params.q = rest;
          const u = new URLSearchParams();
          for (const [k, v] of Object.entries(params)) for (const x of ([] as string[]).concat(v)) u.append(k, x);
          router.push(`/listings?${u.toString()}`);
        })
        .catch(() => { const plain = plainParams(value); router.push(Object.keys(plain).length ? `/listings?${new URLSearchParams(plain).toString()}` : fallback); })
        .finally(() => setHeroBusy(false));
      return;
    }
    const via: "demo" | "typed" | "paste" =
      demoUrl && demoUrl === value ? "demo" : "typed";
    trackEvent("cta.hero.submit", { via });
    // The same valuation the hub's bar opens (lib/listing-url).
    router.push(evaluationHref(value) ?? `/analyze?url=${encodeURIComponent(value)}`);
  }, [demoUrl, heroUrl, router]);

  const handleHeroPaste = useCallback((url: string) => {
    setHeroUrl(url);
    setDemoUrl(null);
    trackEvent("cta.hero.submit", { via: "paste" });
    // Immediately forward to /analyze - matches the share-target UX
    // (no second tap to submit after pasting a known-valid listing).
    router.push(`/analyze?url=${encodeURIComponent(url)}`);
  }, [router]);

  const handleHeroUrlChange = useCallback((next: string) => {
    setHeroUrl(next);
    setDemoUrl(null);
  }, []);

  // A signed-in account opens on its hub, not the landing page (owner,
  // 2026-10-02). With the session cookie present the hub frame renders
  // while auth resolves, so the landing page never flashes first; it is
  // the hub's own module (SignedInHome shows its frame until the account
  // is known), so the server's HTML already preloads the hub's chunks.
  if (user || (signedInHint && authLoading)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SharedHeader />
        <main id="main"><SignedInHome blogPosts={blogPosts} /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      {/* Every page in the product tree has a <main> landmark except
          the homepage did not — Lighthouse's "Document has a main
          landmark" audit failed for /az. Wrapping the hero + flow
          cards + audience section in <main> satisfies the audit and
          makes screen-reader navigation predictable (skip-to-content
          now lands on the hero URL input, not the header). Header,
          install promos, footer, and scroll CTA intentionally sit
          OUTSIDE <main> — they're chrome, not page content. */}
      <main id="main">

      {/* PWA-only teaching surface: a quiet "How to use" pill the
          user can expand any time. Deliberately NOT a first-launch
          takeover - the full-screen welcome modal that used to sit
          here was removed as entry noise (owner call, Aug 2026). */}
      <PWAHomeHelper />

      {/* ── Hero: the headline, one line and the search box over the
          city map. Nothing numeric up here (owner, 2026-10-26); the
          market's figures are one line in the facts block
          (VisitorMarketNow, page.tsx). */}
      <HomeHeroSplit
        url={heroUrl}
        onUrlChange={handleHeroUrlChange}
        onSubmit={submitHero}
        busy={heroBusy}
        onPaste={handleHeroPaste}
        isMobile={isMobile}
        prefillPending={prefillPending}
        intent={visitorIntent}
      />

      <VisitorForYou />

      {/* Signed-in workspace (goal picker + personalized block,
          components/home/HomeWorkspace.tsx) is hidden for now at the
          owner's request (2026-09-28). Re-mount <HomeWorkspace /> here
          to bring it back; the backend goal API stays in place. */}

      {/* Trial surfaces, in priority order: the personalized loss
          guard (last 2 days / just ended), the guide-style offer for
          eligible users, then the first-steps checklist. Each
          self-hides when not applicable. */}
      <TrialOfferCard />

      {/* Goal-gradient first-steps card - fresh signed-in users only
          (self-hides when done/dismissed/anon). Right under the hero
          so the "you're already 20% in" framing lands before the
          marketing sections. */}
      <OnboardingChecklist />

      {/* "What we offer" - the single capabilities section: the flows
          (Listings / Analyze / Map / Residences / Trends / Chat) plus the
          tools strip, so there is ONE feature surface rather than two
          competing grids. The residences carousel is its residences tile
          now (UX plan 3.1, decision 8, 2026-10-29). */}
      <HomeCoreFlowsRow intent={visitorIntent} />

      {/* The one carousel: recent valuations, social proof and a funnel
          into /listings. The market card under the tools became a line in
          the facts block, and the residences row a tile above (UX plan
          3.1, 2026-10-29: two carousels said the same thing). Self-hides
          when the API returns no items, so we don't reserve a band for
          nothing. */}
      <div className="overflow-x-clip">
        <Reveal from="left">
          <RecentlyAnalyzedCarousel />
        </Reveal>
      </div>

      {/* Horizontal section separator. Thin rule inside the content
          container so it breathes with the page max-width rather
          than running edge-to-edge. Generous vertical spacing on
          either side to give each section a clear anchor. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <hr className="border-t border-gray-200" />
      </div>

      {/* The long-form SEO content block that previously sat here was
          removed once the same topic shipped as a dedicated blog post
          (`/[locale]/blog/baku-market-guide-2026`). Two pages on the
          same domain with identical headings and overlapping copy
          cannibalise each other in Google's ranking; the blog post
          has BlogPosting JSON-LD, its own URL, and a sitemap entry,
          so it's the better canonical. The "Latest from the blog"
          strip below already surfaces it from the homepage, which
          concentrates internal-link authority on the post. */}

      {factsBlock}

      <HomeBlogSection posts={blogPosts} />

      {/* "Why us" 2x2 differentiator grid removed - three prose
          sections in a row (audience tiles + why-us + final CTA copy)
          read as a marketing wall. The data + trust + speed claims
          live more honestly inside the offering cards' descriptions
          and the hero stats. */}

      {/* Final CTA: the inline gradient panel and the later
          scroll-triggered modal are both gone. Account capture happens
          at the moment of value instead (ResultStayUpdated,
          TrackHomeCta, the alert form), per the 2026-06-24 plan. */}
      </main>

      {/* The phone acquisition path (self-gates by UA). The desktop
          Chrome-extension banner that sat beside it is gone (owner,
          2026-10-18). */}
      <PWAInstallPromo />

      {/* The welcome card is WelcomeHost's now (layout): it shows on
          whichever page a new account lands on, this one included. */}
    </div>
  );
}

